import L from 'leaflet';
import { distance as turfDistance } from '@turf/turf';

const TYPE_COLORS = {
    work: '#7FC200',
    resettle: '#6c757d',
    deadhead: '#ff7f0e',
    derived: '#3388ff',
    unknown: '#444'
};

export const routeOptimizationMixin = {
    data() {
        return {
            workflowStep: 'setup',
            highlightedVehicle: null,
        }
    },

    created() {
        // Non-reactive animation state — not in data() intentionally
        this.lastFrameTime = null;
        this._highlightPolyline = null;
        this._highlightPopupLayer = null;
    },
    methods: {
        /* --- network --- */
        loadSimulationFromStore() {
            const simArray = this.$store.state.routeOptimization.routeOptimizationResult;

            if (!simArray || simArray.length === 0) return;

            this.clearMap();
            this.vehicles = [];

            simArray.forEach((simObj, vehicleIndex) => {
                const vehicleData = this.initializeSimulation(simObj, vehicleIndex);
                this.vehicles.push(vehicleData);
            });

            // Fit map to all vehicles' routes once, after all are drawn
            this.fitMapToTour();

            // Ensure work polylines are always on top of transit/resettle after
            // all vehicles have been drawn — bringToFront during the loop is not
            // enough because subsequent addTo() calls stack above it.
            this.bringWorkLayersToFront();

            this.startPlayback();
        },

        /* --- simulation & drawing --- */
        initializeSimulation(obj, vehicleIndex) {
            const steps = obj.geojson.features;
            const vehicleColor = obj.metadata.vehicleColor || '#7FC200';

            steps.forEach((f) => {
                const coords = f.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
                const t = (f.properties.traversal_type || 'unknown').toLowerCase();
                const color = t === 'work' ? vehicleColor : TYPE_COLORS[t];
                const weight = t === 'work' ? 5 : 4;

                const pl = L.polyline(coords, {
                    color,
                    weight,
                    opacity: 0.95
                }).addTo(this.tourLayer);

                // Click opens a Leaflet popup with formatted/translated content
                // and draws a dashed white highlight over the segment.
                pl.on('click', () => {
                    this.onSegmentClick(pl, coords, {
                        type: t,
                        length: f.properties.length,
                        travel_duration: f.properties.travel_duration,
                        travel_speed: f.properties.travel_speed,
                    });
                });

                this.stepPolylines.push({ layer: pl, type: t, vehicleIndex, vehicleColor });
                this.layersByType[t] = this.layersByType[t] || [];
                this.layersByType[t].push(pl);

                if (t === 'work') {
                    pl.bringToFront();
                }
            });

            const timeline = this.buildTimelineFromTour(steps);
            // Pre-compute total duration in simulation-seconds for this vehicle
            // so the time display doesn't have to re-sum on every frame.
            const totalDurationSec = timeline.reduce((sum, seg) => sum + seg.durationSec, 0);

            return {
                timeline,
                timelineIndex: 0,
                elapsedInSegment: 0,
                elapsedTotalSec: 0,   // accumulated real simulation-seconds for this vehicle
                totalDurationSec,
                marker: null,
                color: vehicleColor,
                index: vehicleIndex
            };
        },

        buildTimelineFromTour(features) {
            const tl = [];

            for (let s = 0; s < features.length; s++) {
                const f = features[s];
                // Coordinates in GeoJSON are [lon, lat]; Leaflet needs [lat, lon]
                const conv = f.geometry.coordinates.map(([lon2, lat2]) => [lat2, lon2]);

                if (conv.length < 2) continue;

                // travel_speed is in km/h — convert to m/s for duration calculation.
                // The fallback (10 km/h) only fires if the backend omits travel_speed entirely;
                // it is intentionally conservative to avoid making any segment appear unrealistically fast.
                const speedKmh = f.properties.travel_speed || 10;
                const speedMs = speedKmh / 3.6;
                const traversalType = (f.properties.traversal_type || 'unknown').toLowerCase();

                for (let i = 0; i < conv.length - 1; i++) {
                    // Use turf to calculate the actual geographic distance of each
                    // sub-segment in meters. This ensures the marker moves at a speed
                    // that is proportional to the real distance, regardless of how
                    // densely or sparsely the backend has sampled the coordinate array.
                    const fromPoint = [conv[i][1], conv[i][0]];   // turf expects [lon, lat]
                    const toPoint   = [conv[i + 1][1], conv[i + 1][0]];
                    const segDistanceM = turfDistance(fromPoint, toPoint, { units: 'kilometers' }) * 1000;

                    // duration = distance / speed (both in metres and m/s)
                    const durationSec = segDistanceM / speedMs;

                    tl.push({
                        type: 'move',
                        from: conv[i],
                        to: conv[i + 1],
                        // Clamp to a small minimum so zero-length duplicate coords
                        // don't cause division-by-zero in the animation loop
                        durationSec: Math.max(0.01, durationSec),
                        traversal_type: traversalType,
                        stepIndex: s
                    });
                }
            }
            return tl;
        },

        fitMapToTour() {
            const grp = L.featureGroup();

            if (this.tourLayer) this.tourLayer.eachLayer(l => grp.addLayer(l));

            if (grp.getLayers().length) this.map.fitBounds(grp.getBounds().pad(0.18));
        },

        clearMap() {
            if (this.tourLayer) this.tourLayer.clearLayers();
            if (this.traceLayer) this.traceLayer.clearLayers();
            if (this.highlightLayer) this.highlightLayer.clearLayers();

            this.stepPolylines = [];
            this.layersByType = {};
            this.vehicles = [];
        },

        createDotIcon(color, size = 18) {
            return {
                radius: size / 2,
                fillColor: color,
                color: '#fff',
                weight: 2,
                opacity: 1,
                fillOpacity: 1,
                zIndexOffset: 1000
            };
        },

        getTraversalTypeForIndex(idx) {
            // Legacy single-vehicle helper — not used in multi-vehicle simulation.
            // Retained to avoid breaking any external callers.
            if (!this.timeline || this.timeline.length === 0) return null;
            idx = Math.max(0, Math.min(this.timeline.length - 1, idx));
            for (let i = idx; i >= 0; i--) if (this.timeline[i].traversal_type) return this.timeline[i].traversal_type;
            return null;
        },

        getPositionAtTimelineIndex(index, tInSegment) {
            // Legacy single-vehicle helper — not used in multi-vehicle simulation.
            // Retained to avoid breaking any external callers.
            if (index < 0) index = 0;
            if (index >= this.timeline.length) index = this.timeline.length - 1;
            const seg = this.timeline[index];
            if (!seg) return [0, 0];
            if (seg.type === 'pause') return seg.at;
            const frac = Math.max(0, Math.min(1, tInSegment / seg.durationSec));
            const lat = seg.from[0] + (seg.to[0] - seg.from[0]) * frac;
            const lon = seg.from[1] + (seg.to[1] - seg.from[1]) * frac;
            return [lat, lon];
        },

        animate(timestamp) {
            if (!this.playback.playing) return;
            if (!this.lastFrameTime) this.lastFrameTime = timestamp;

            const dtMs = timestamp - this.lastFrameTime;

            this.lastFrameTime = timestamp;

            const dtSec = (dtMs / 1000) * this.playback.speed;

            let allFinished = true;

            this.vehicles.forEach(vehicle => {
                if (vehicle.timelineIndex >= vehicle.timeline.length) return;

                allFinished = false;
                vehicle.elapsedInSegment += dtSec;
                vehicle.elapsedTotalSec += dtSec;

                while (vehicle.timelineIndex < vehicle.timeline.length) {
                    const seg = vehicle.timeline[vehicle.timelineIndex];

                    if (seg.type === 'pause') {
                        if (vehicle.elapsedInSegment >= seg.durationSec) {
                            vehicle.elapsedInSegment -= seg.durationSec;
                            vehicle.timelineIndex++;
                        } else {
                            if (vehicle.marker) vehicle.marker.setLatLng(seg.at);
                            break;
                        }
                    } else {
                        if (vehicle.elapsedInSegment >= seg.durationSec) {
                            if (vehicle.marker) vehicle.marker.setLatLng(seg.to);

                            vehicle.elapsedInSegment -= seg.durationSec;
                            vehicle.timelineIndex++;
                        } else {
                            const frac = Math.max(0, Math.min(1, vehicle.elapsedInSegment / seg.durationSec));
                            const lat = seg.from[0] + (seg.to[0] - seg.from[0]) * frac;
                            const lon = seg.from[1] + (seg.to[1] - seg.from[1]) * frac;
                            const currentColor = seg.traversal_type === 'work' ? vehicle.color : '#6c757d';
                            const isVisible = this.selectedVehicle === null || this.selectedVehicle?.index === vehicle.index;

                            if (vehicle.marker) {
                                if (isVisible) {
                                    vehicle.marker.setLatLng([lat, lon]);
                                    vehicle.marker.setStyle(this.createDotIcon(currentColor, 18));
                                    if (!this.traceLayer.hasLayer(vehicle.marker)) {
                                        vehicle.marker.addTo(this.traceLayer);
                                    }
                                } else {
                                    if (this.traceLayer.hasLayer(vehicle.marker)) {
                                        this.traceLayer.removeLayer(vehicle.marker);
                                    }
                                }
                            } else {
                                vehicle.marker = L.circleMarker([lat, lon], this.createDotIcon(currentColor, 18));
                                if (isVisible) {
                                    vehicle.marker.addTo(this.traceLayer);
                                }
                            }
                            break;
                        }
                    }
                }
            });

            // Update scrubber position using real elapsed simulation-seconds of the
            // slowest (longest) vehicle so the timer reflects actual progress.
            // Floor to whole seconds so the reactive label only re-renders once per
            // second rather than on every animation frame. Clamp to maxDuration so
            // the scrubber thumb never overshoots the end on the final frame.
            if (this.vehicles.length > 0) {
                const maxDuration = Math.max(...this.vehicles.map(v => v.totalDurationSec || 0));
                const maxElapsed = Math.max(...this.vehicles.map(v => v.elapsedTotalSec || 0));
                this.manualTimelinePosition = Math.min(Math.floor(maxElapsed), Math.floor(maxDuration));
            }

            if (allFinished) {
                this.playback.playing = false;

                if (this.playback.loop) {
                    setTimeout(() => {
                        this.resetPlayback();
                        this.playback.playing = true;
                        this.lastFrameTime = null;
                        requestAnimationFrame((ts) => this.animate(ts));
                    }, 700);
                }
            } else {
                requestAnimationFrame((ts) => this.animate(ts));
            }
        },

        resetPlayback() {
            this.playback.playing = false;
            this.lastFrameTime = null;

            if (this.traceLayer) this.traceLayer.clearLayers();

            this.vehicles.forEach(vehicle => {
                vehicle.timelineIndex = 0;
                vehicle.elapsedInSegment = 0;
                vehicle.elapsedTotalSec = 0;

                if (vehicle.marker && this.map) {
                    this.map.removeLayer(vehicle.marker);
                }
                if (vehicle.timeline.length > 0) {
                    const firstSeg = vehicle.timeline[0];
                    const pos = firstSeg.type === 'pause' ? firstSeg.at : firstSeg.from;
                    vehicle.marker = L.circleMarker(pos, this.createDotIcon(vehicle.color, 18)).addTo(this.traceLayer);
                }
            });
        },

        startPlayback() {
            if (!this.vehicles || this.vehicles.length === 0) return;

            this.playback.playing = true;
            this.lastFrameTime = null;
            // Do NOT reset elapsedInSegment here — if the user scrubbed to a position
            // before hitting play, elapsedInSegment already holds the correct intra-segment
            // offset. Resetting it would snap the marker back to seg.from.

            requestAnimationFrame((ts) => this.animate(ts));
        },

        pausePlayback() {
            this.playback.playing = false;
        },

        seekToSeconds(targetSeconds) {
            if (!this.vehicles || this.vehicles.length === 0) return;

            const maxDuration = Math.max(...this.vehicles.map(v => v.totalDurationSec || 0));
            const clamped = Math.max(0, Math.min(targetSeconds, maxDuration));

            this.manualTimelinePosition = Math.floor(clamped);

            this.vehicles.forEach(vehicle => {
                if (!vehicle.timeline || vehicle.timeline.length === 0) return;

                let accumulated = 0;
                let found = false;

                for (let i = 0; i < vehicle.timeline.length; i++) {
                    const seg = vehicle.timeline[i];
                    if (accumulated + seg.durationSec >= clamped) {
                        vehicle.timelineIndex = i;
                        vehicle.elapsedInSegment = clamped - accumulated;
                        vehicle.elapsedTotalSec = clamped;
                        found = true;
                        break;
                    }
                    accumulated += seg.durationSec;
                }

                if (!found) {
                    vehicle.timelineIndex = vehicle.timeline.length - 1;
                    vehicle.elapsedInSegment = 0;
                    vehicle.elapsedTotalSec = vehicle.totalDurationSec;
                }

                if (vehicle.marker && this.traceLayer) {
                    this.traceLayer.removeLayer(vehicle.marker);
                }

                const seg = vehicle.timeline[vehicle.timelineIndex];
                if (seg) {
                    const pos = seg.type === 'pause'
                        ? seg.at
                        : (() => {
                            const frac = Math.max(0, Math.min(1, vehicle.elapsedInSegment / seg.durationSec));
                            return [
                                seg.from[0] + (seg.to[0] - seg.from[0]) * frac,
                                seg.from[1] + (seg.to[1] - seg.from[1]) * frac
                            ];
                        })();
                    vehicle.marker = L.circleMarker(pos, this.createDotIcon(vehicle.color, 18));

                    if (this.selectedVehicle === null || this.selectedVehicle?.index === vehicle.index) {
                        vehicle.marker.addTo(this.traceLayer);
                    }
                }
            });
        },

        stepForward() {
            if (!this.vehicles || this.vehicles.length === 0) return;
            const current = Math.max(...this.vehicles.map(v => v.elapsedTotalSec || 0));
            this.seekToSeconds(current + 30);
        },

        stepBack() {
            if (!this.vehicles || this.vehicles.length === 0) return;
            const current = Math.max(...this.vehicles.map(v => v.elapsedTotalSec || 0));
            this.seekToSeconds(current - 30);
        },

        highlightVehicle(vehicleIndex) {
            // If null, reset all
            if (vehicleIndex === null) {
                this.resetHighlight();
                return;
            }

            // If same vehicle is already highlighted, reset instead
            if (this.highlightedVehicle === vehicleIndex) {
                this.resetHighlight();
                return;
            }

            this.highlightedVehicle = vehicleIndex;

            this.stepPolylines.forEach(o => {
                if (o.vehicleIndex === vehicleIndex) {
                    const t = o.type;
                    o.layer.setStyle({
                        opacity: 1.0,
                        weight: (t === 'work') ? 5 : 4
                    });
                    if (!this.tourLayer.hasLayer(o.layer)) {
                        o.layer.addTo(this.tourLayer);
                    }
                    o.layer.bringToFront();
                } else {
                    this.tourLayer.removeLayer(o.layer);
                }
            });

            this.vehicles.forEach(vehicle => {
                if (vehicle.marker) {
                    if (vehicle.index === vehicleIndex) {
                        vehicle.marker.setStyle({ opacity: 1.0, fillOpacity: 1.0 });

                        if (!this.traceLayer.hasLayer(vehicle.marker)) {
                            this.traceLayer.addLayer(vehicle.marker);
                        }
                    } else {
                        if (this.traceLayer.hasLayer(vehicle.marker)) {
                            this.traceLayer.removeLayer(vehicle.marker);
                        }
                    }
                }
            });

            // bringWorkLayersToFront also brings traceLayer markers to front last,
            // so the dot always ends up above the polylines.
            this.bringWorkLayersToFront();
        },

        resetHighlight() {
            this.highlightedVehicle = null;
            this.stepPolylines.forEach(o => {
                const key = `${o.vehicleIndex}-${o.type}`;
                const isHidden = this.hiddenLayers && this.hiddenLayers[key];
                const t = o.type;
                const color = (t === 'work') ? o.vehicleColor : (TYPE_COLORS[t] || TYPE_COLORS.unknown);
                const weight = (t === 'work') ? 5 : 4;

                if (isHidden) {
                    // Keep removed from map if toggled off via sub-legend
                    this.tourLayer.removeLayer(o.layer);
                } else {
                    o.layer.setStyle({ color, weight, opacity: 0.95, fillOpacity: 0.95 });
                    if (!this.tourLayer.hasLayer(o.layer)) {
                        o.layer.addTo(this.tourLayer);
                    }
                }
            });

            this.vehicles.forEach(vehicle => {
                if (vehicle.marker) {
                    vehicle.marker.setStyle({ opacity: 1.0, fillOpacity: 1.0 });
                    vehicle.marker.bringToFront();

                    if (!this.traceLayer.hasLayer(vehicle.marker)) {
                        this.traceLayer.addLayer(vehicle.marker);
                    }
                }
            });

            this.bringWorkLayersToFront();
        },

        onSegmentClick(pl, coords, props) {
            // Clear any previous highlight and close previous popup
            this.clearSegmentHighlight();

            // Draw the dashed white overlay matching the setup view mask style
            if (this.highlightLayer) {
                this._highlightPolyline = L.polyline(coords, {
                    color: '#ffffff',
                    weight: 2,
                    opacity: 1,
                    dashArray: '6, 5',
                }).addTo(this.highlightLayer);
            }

            // Keep a reference so clearSegmentHighlight can close this popup directly
            this._highlightPopupLayer = pl;

            // Build a translated, formatted Leaflet popup.
            // $t() is available here because the mixin runs on the Vue instance.
            const typeLabel = this.$t(`route_optimization.${props.type}`);
            const lengthLabel = this.$t('route_optimization.length');
            const durationLabel = this.$t('route_optimization.duration');
            const speedLabel = this.$t('route_optimization.speed');

            const lengthVal = props.length >= 1000
                ? (props.length / 1000).toFixed(2) + ' km'
                : Math.round(props.length) + ' m';

            // Duration in minutes and seconds for segment-level granularity
            const totalSec = Math.max(0, Math.round(props.travel_duration || 0));
            const durMin = Math.floor(totalSec / 60);
            const durSec = totalSec % 60;
            const durationVal = `${durMin}m ${durSec.toString().padStart(2, '0')}s`;

            const speedVal = props.travel_speed ? Math.round(props.travel_speed) + ' km/h' : '-';

            const row = (label, val) =>
                `<tr><td style="color:#888;width:70px;white-space:nowrap">${label}</td>` +
                `<td style="font-weight:500;white-space:nowrap;padding-left:8px">${val}</td></tr>`;

            const content =
                `<b style="text-transform:capitalize">${typeLabel}</b>` +
                `<table style="margin-top:4px;border-collapse:collapse">` +
                row(lengthLabel, lengthVal) +
                row(durationLabel, durationVal) +
                row(speedLabel, speedVal) +
                `</table>`;

            pl.bindPopup(content, { autoPan: false }).openPopup();

            // Remove highlight when popup is closed by the user
            pl.once('popupclose', () => this.clearSegmentHighlight());
        },

        clearSegmentHighlight() {
            if (this.highlightLayer) this.highlightLayer.clearLayers();
            // Close the popup on the specific polyline that opened it
            if (this._highlightPopupLayer) {
                this._highlightPopupLayer.off('popupclose'); // prevent recursive call
                this._highlightPopupLayer.closePopup();
                this._highlightPopupLayer = null;
            }
            this._highlightPolyline = null;
        },

        resetMap() {
            this.clearMap();
        },

        bringWorkLayersToFront() {
            // Call after any operation that changes the layer stack.
            // First pass: non-work layers (resettle, deadhead, etc.) — only those currently on the map
            // Second pass: work layers — ensures work is always visually on top.
            this.stepPolylines.forEach(o => {
                if (o.type !== 'work' && this.tourLayer.hasLayer(o.layer)) o.layer.bringToFront();
            });
            this.stepPolylines.forEach(o => {
                if (o.type === 'work' && this.tourLayer.hasLayer(o.layer)) o.layer.bringToFront();
            });
            // Finally bring all moving dot markers above the polylines so they
            // are never obscured by routes.
            if (this.traceLayer) {
                this.traceLayer.eachLayer(l => l.bringToFront());
            }
        },
    }

}
